import { Link, useParams } from "react-router-dom";
import Seo from "@/components/base/Seo";
import EventCard, { formatViews } from "@/components/feature/EventCard";
import { hotEvents } from "@/mocks/events";

export default function EventDetailPage() {
  const { slug } = useParams();
  const event = hotEvents.find((e) => e.slug === slug);

  if (!event) {
    return (
      <section className="mx-auto flex min-h-[60vh] max-w-[1280px] flex-col items-center justify-center px-4 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">该事件不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的现场实拍事件可能已被移除，去看看其他热点吧。</p>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-600">
          返回首页 <i className="ri-arrow-right-line"></i>
        </Link>
      </section>
    );
  }

  const related = hotEvents.filter((e) => e.category === event.category && e.slug !== event.slug).slice(0, 3);
  const hotSide = [...hotEvents].sort((a, b) => b.views - a.views).filter((e) => e.slug !== event.slug).slice(0, 4);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: event.title,
      description: event.summary,
      inLanguage: "zh-CN",
      datePublished: event.time,
      articleSection: event.categoryName,
      keywords: event.tags.join(","),
      url: `https://hllpxyz.cn/event/${event.slug}`,
      author: { "@type": "Organization", name: "吃瓜视频在线" },
      image: event.thumb,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: "https://hllpxyz.cn/" },
        { "@type": "ListItem", position: 2, name: event.categoryName, item: `https://hllpxyz.cn/category/${event.category}` },
        { "@type": "ListItem", position: 3, name: event.title, item: `https://hllpxyz.cn/event/${event.slug}` },
      ],
    },
  ];

  return (
    <>
      <Seo title={`${event.title} - 吃瓜视频在线`} description={event.summary} keywords={event.tags.join(",")} path={`/event/${event.slug}`} jsonLd={jsonLd} />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${event.category}`} className="transition hover:text-primary-500">{event.categoryName}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="line-clamp-1 text-foreground-300">{event.title}</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* 主体 */}
          <div className="lg:col-span-2">
            {/* 视频播放区 */}
            <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
              <img src={event.thumb} alt={`${event.title}现场实拍视频封面`} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/30 to-background-950/20"></div>
              <button
                type="button"
                onClick={() => window.alert("演示环境：完整现场视频将在接入真实数据源后播放")}
                className="absolute inset-0 flex items-center justify-center"
                aria-label="播放现场实拍视频"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500 text-background-50 transition hover:scale-105 md:h-20 md:w-20">
                  <i className="ri-play-fill pl-1 text-3xl md:text-4xl"></i>
                </span>
              </button>
              <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 md:p-5">
                <span className="flex items-center gap-2 text-xs text-foreground-300 md:text-sm">
                  <i className="ri-map-pin-2-line text-primary-400"></i>{event.location}
                </span>
                <span className="flex items-center gap-1.5 rounded bg-background-950/80 px-2.5 py-1 text-xs font-medium text-foreground-200">
                  <i className="ri-time-line"></i>{event.duration}
                </span>
              </div>
            </div>

            <div className="mt-6">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-800">
                <i className="ri-fire-line"></i>{event.categoryName}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">{event.title}</h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-5 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{event.time}</span>
                <span className="flex items-center gap-1.5"><i className="ri-eye-line"></i>{formatViews(event.views)}次观看</span>
                <span className="flex items-center gap-1.5"><i className="ri-map-pin-2-line"></i>{event.location}</span>
              </div>

              <p className="mt-6 rounded-lg border-l-2 border-primary-500 bg-background-100 p-5 text-sm leading-relaxed text-foreground-700">
                {event.summary}
              </p>

              <article className="mt-6 space-y-5">
                {event.content.map((para, i) => (
                  <p key={i} className="text-[15px] leading-8 text-foreground-600">
                    {para}
                  </p>
                ))}
              </article>

              <div className="mt-8 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground-500">话题标签：</span>
                {event.tags.map((t) => (
                  <Link key={t} to={`/category?q=${encodeURIComponent(t)}`} className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition hover:bg-secondary-500 hover:text-background-50">
                    #{t}
                  </Link>
                ))}
              </div>

              <p className="mt-6 text-xs leading-relaxed text-foreground-500">
                温馨提示：本平台内容为现场实拍与路人记录，仅作信息整理与事件还原，不构成对任何个人或组织的评价与定性。请理性观看、客观看待争议，勿参与网络攻击。
              </p>
            </div>
          </div>

          {/* 侧栏 */}
          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关推荐</h2>
              <div className="mt-4 space-y-4">
                {related.length > 0 ? related.map((e) => (
                  <Link key={e.slug} to={`/event/${e.slug}`} className="group flex gap-3">
                    <span className="relative block h-[64px] w-[96px] shrink-0 overflow-hidden rounded-md">
                      <img src={e.thumb} alt={`${e.title}现场实拍`} className="h-full w-full object-cover transition duration-300 group-hover:scale-105" />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-sm font-bold leading-snug text-foreground-800 group-hover:text-primary-500">{e.title}</span>
                      <span className="mt-1 block text-xs text-foreground-500">{formatViews(e.views)}次观看</span>
                    </span>
                  </Link>
                )) : (
                  <p className="text-sm text-foreground-500">暂无更多同类事件。</p>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">热门看点</h2>
              <div className="mt-4 space-y-3">
                {hotSide.map((e, i) => (
                  <Link key={e.slug} to={`/event/${e.slug}`} className="group flex items-center gap-3">
                    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-black ${i < 3 ? "bg-accent-500 text-foreground-950" : "bg-background-200 text-foreground-600"}`}>{i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-1 text-sm font-medium text-foreground-800 group-hover:text-primary-500">{e.title}</span>
                      <span className="mt-0.5 block text-xs text-foreground-500">{formatViews(e.views)}次观看</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* 更多事件 */}
        <div className="mt-14">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">猜你还想看</h2>
            <Link to="/category" className="flex items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition hover:text-primary-500">
              更多事件 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {hotEvents.filter((e) => e.slug !== event.slug).slice(0, 3).map((e) => (
              <EventCard key={e.slug} event={e} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}